import React from 'react';
import { shallow } from 'zustand/shallow';
import type { Card, Effect } from '../game/types';
import combatMask1Img from '../../assets/damage/combat_mask1.png';
import combatMask2Img from '../../assets/damage/combat_mask2.png';
import combatMask3Img from '../../assets/damage/combat_mask3.png';
import combatMask4Img from '../../assets/damage/combat_mask4.png';
import combatMask5Img from '../../assets/damage/combat_mask5.png';
import combatMask6Img from '../../assets/damage/combat_mask6.png';
import combatMask1OverlayImg from '../../assets/damage/combat_mask1b.png';
import combatMask2OverlayImg from '../../assets/damage/combat_mask2b.png';
import combatMask3OverlayImg from '../../assets/damage/combat_mask3b.png';
import combatMask4OverlayImg from '../../assets/damage/combat_mask4b.png';
import combatMask5OverlayImg from '../../assets/damage/combat_mask5b.png';
import combatMask6OverlayImg from '../../assets/damage/combat_mask6b.png';
// Escalation counters still come from props, but we also read a small slice of
// combat state so Blackhand-specific overload costs can be reflected in the UI.
import CardKeywordTooltip, { type CardTooltipExtraRow } from './CardKeywordTooltip';
import { useGameStore } from '../store/useGameStore';

type Tag = { label: string; tone?: 'info' | 'warn' | 'danger' };


type CardCostUiState = {
  nextCardCost0: number;
  nextMagicCardCostDoubleSP: number;
  utilityCostReductionThisTurn: number;
  chainCostReduction: number;
  delayCostReduction: number;
  exileCardsGetChain: boolean;
  turn: number;
  phase: string;
  blackhandPressurePointTurn: number;
  playerMarkedByBlackhand: boolean;
};

type ComputedCardCostUi = {
  cardTone: 'light' | 'dark';
  hpBadgeText: string;
  spBadgeText: string;
  hasPressurePointHpBonus: boolean;
  hasPressurePointSpEcho: boolean;
  extraTooltipRows: CardTooltipExtraRow[];
};

function hasKeywordText(card: Card, keyword: string): boolean {
  const target = String(keyword).trim().toLowerCase();
  return (card.keywords ?? []).some((entry) => String(entry).trim().toLowerCase() === target);
}

function buildSymbolicBase(label: 'X', printedExtra: number): string {
  return printedExtra > 0 ? `X+${printedExtra}` : label;
}

function formatBadgeValue(base: number | string, extras: number[]): string {
  const appliedExtras = extras.filter((value) => value > 0);
  if (typeof base === 'number') {
    return String(base + appliedExtras.reduce((sum, value) => sum + value, 0));
  }
  return `${base}${appliedExtras.map((value) => `+${value}`).join('')}`;
}

function computeCardCostUi(card: Card, state: CardCostUiState): ComputedCardCostUi {
  const hpIsX = !!(card as any).hpCostIsX;
  const spIsX = !!(card as any).spCostIsX;
  const cost0Active = state.nextCardCost0 > 0;
  const isUtilityCard = String((card as any).tag ?? '').trim().toLowerCase() === 'utility' || /\butility\b/i.test(String(card.subtitle ?? ''));
  const utilityReduction = !cost0Active && isUtilityCard ? Math.max(0, Number(state.utilityCostReductionThisTurn) || 0) : 0;

  const hasDelay = Math.max(0, Number((card as any).delayTurns || 0) || 0) > 0
    || (Array.isArray((card as any).delayed) && (card as any).delayed.length > 0)
    || (Math.max(0, Number((card as any).costDelayTurns || 0) || 0) > 0);

  const hasChainKeyword = hasKeywordText(card, 'Chain');
  const hasExileKeyword = hasKeywordText(card, 'Exile');
  const chainEligible = hasChainKeyword || (state.exileCardsGetChain && hasExileKeyword);

  const printedHp = Math.max(0, Number(card.cost?.hp ?? 0) || 0);
  const reducedPrintedHp = utilityReduction > 0 ? Math.max(0, printedHp - utilityReduction) : printedHp;
  const hpBaseNumeric = cost0Active ? 0 : reducedPrintedHp;

  const printedSp = Math.max(0, Number(card.cost?.sp ?? 0) || 0);
  const reducedPrintedSpFromUtility = utilityReduction > 0 ? Math.max(0, printedSp - utilityReduction) : printedSp;
  const reducedPrintedSpFromChain = !cost0Active && chainEligible && state.chainCostReduction > 0
    ? Math.max(0, reducedPrintedSpFromUtility - state.chainCostReduction)
    : reducedPrintedSpFromUtility;
  const magicDoubleActive = !cost0Active && card.kind === 'magic' && state.nextMagicCardCostDoubleSP > 0;
  const scaledSpBeforeDelay = cost0Active ? 0 : reducedPrintedSpFromChain * (magicDoubleActive ? 2 : 1);
  const spBaseBeforeOverloadNumeric = !cost0Active && hasDelay && state.delayCostReduction > 0
    ? Math.max(0, scaledSpBeforeDelay - state.delayCostReduction)
    : scaledSpBeforeDelay;
  const overloadAmount = !cost0Active && card.kind === 'magic'
    ? Math.max(0, Number((card as any).__uiMagicChainStreak ?? 0) || 0)
    : 0;

  const pressurePointActive = state.phase === 'player' && state.blackhandPressurePointTurn === state.turn;
  const pressurePointMultiplier = pressurePointActive && overloadAmount > 0
    ? (state.playerMarkedByBlackhand ? 2 : 1)
    : 0;
  const pressurePointHpBonus = overloadAmount > 0 ? overloadAmount * pressurePointMultiplier : 0;
  const pressurePointExtraSp = overloadAmount > 0 ? overloadAmount * Math.max(0, pressurePointMultiplier - 1) : 0;

  const hpBaseLabel: number | string = hpIsX && !cost0Active
    ? buildSymbolicBase('X', printedHp)
    : hpBaseNumeric;
  const spBaseLabel: number | string = spIsX && !cost0Active
    ? buildSymbolicBase('X', printedSp)
    : spBaseBeforeOverloadNumeric;

  const extraTooltipRows: CardTooltipExtraRow[] = [];
  if (overloadAmount > 0) {
    extraTooltipRows.push({
      key: `chain-overload-${card.id}`,
      label: 'Chain Overload',
      description: `${spBaseLabel}+${overloadAmount} SP${pressurePointExtraSp > 0 ? ` (+${pressurePointExtraSp} SP from Pressure Point)` : ''}`,
    });
  }
  if (pressurePointHpBonus > 0) {
    extraTooltipRows.push({
      key: `pressure-point-hp-${card.id}`,
      label: 'Pressure Point',
      description: `+${pressurePointHpBonus} HP`,
    });
  }

  return {
    cardTone: card.kind === 'magic' ? 'dark' : 'light',
    hpBadgeText: formatBadgeValue(hpBaseLabel, [pressurePointHpBonus]),
    spBadgeText: formatBadgeValue(spBaseLabel, [overloadAmount, pressurePointExtraSp]),
    hasPressurePointHpBonus: pressurePointHpBonus > 0,
    hasPressurePointSpEcho: pressurePointExtraSp > 0,
    extraTooltipRows,
  };
}

// Props for the CardView component. Defining this explicit type lets us
// write a custom comparison function for React.memo below, ensuring that
// CardView only re-renders when a relevant prop changes. Without this,
// Hand and Board animation ticks can cause CardView to render far more
// often than necessary.
export type CardViewProps = {
  card: Card;
  onClick?: () => void;
  disabled?: boolean;
  mode?: 'play' | 'discard';
  showKeywordTooltip?: boolean;
  /** The physical escalation counter (number of times the physical deck reshuffled). */
  physicalEscalation?: number;
  /** The magic escalation counter (number of times the magic deck reshuffled). */
  magicEscalation?: number;
  /** The current magic chain streak counter. */
  magicChainStreak?: number;
};

/**
 * Compare two CardView props objects. React.memo will use this comparator
 * to determine whether CardView needs to update. We compare cards by
 * reference and all other props by strict equality. This prevents
 * unnecessary re-renders when parent components update unrelated state.
 */
function areCardViewPropsEqual(prev: CardViewProps, next: CardViewProps) {
  return (
    prev.card === next.card &&
    prev.onClick === next.onClick &&
    prev.disabled === next.disabled &&
    prev.mode === next.mode &&
    prev.showKeywordTooltip === next.showKeywordTooltip &&
    prev.physicalEscalation === next.physicalEscalation &&
    prev.magicEscalation === next.magicEscalation &&
    prev.magicChainStreak === next.magicChainStreak
  );
}

const COMBAT_DAMAGE_MASKS = [
  combatMask1Img,
  combatMask2Img,
  combatMask3Img,
  combatMask4Img,
  combatMask5Img,
  combatMask6Img,
] as const;

const COMBAT_DAMAGE_MASK_OVERLAYS = [
  combatMask1OverlayImg,
  combatMask2OverlayImg,
  combatMask3OverlayImg,
  combatMask4OverlayImg,
  combatMask5OverlayImg,
  combatMask6OverlayImg,
] as const;

function hashToIndex(value: string, count: number): number {
  if (!count) return -1;
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0) % count;
}

function uniqSorted(nums: number[]): number[] {
  const s = Array.from(new Set(nums.filter((n) => Number.isFinite(n))));
  s.sort((a, b) => a - b);
  return s;
}

function buildTags(card: Card): Tag[] {
  const tags: Tag[] = [];

  // Core keywords
  const kw = new Set((card.keywords ?? []).map((k) => k.trim()));
  if (kw.has('Chain')) tags.push({ label: 'CHAIN', tone: 'info' });
  if (kw.has('Finisher')) tags.push({ label: 'FINISHER', tone: 'danger' });
  if (kw.has('Exile')) tags.push({ label: 'EXILE', tone: 'warn' });

  // Retain
  if (card.retainAfterPlay) tags.push({ label: 'RETAIN', tone: 'info' });

  // Delay segments
  const delays = uniqSorted((card.delayed ?? []).map((d) => d.turns));
  if (delays.length) tags.push({ label: `DELAY ${delays.join(',')}`, tone: 'warn' });

  // Delayed COST (e.g. Borrowed Dawn)
  const costDelay = Math.max(0, Number((card as any).costDelayTurns ?? 0) || 0);
  if (costDelay > 0) tags.push({ label: `COST DELAY ${costDelay}`, tone: 'warn' });

  // Effects-derived tags (just the "decision" ones)
  const effs = card.effects ?? [];
  const get = <T extends Effect['type']>(t: T) => effs.filter((e) => e.type === t);

  const disc = get('discard');
  if (disc.length) {
    const n = disc.reduce((a: number, e: any) => a + (e.amount || 0), 0);
    if (n > 0) tags.push({ label: `DISCARD ${n}`, tone: 'danger' });
  }

  const draw = get('draw');
  const nDraw = draw.reduce((a: number, e: any) => a + (e.amount || 0), 0);
  if (nDraw > 0) tags.push({ label: `DRAW ${nDraw}`, tone: 'info' });

  const dmg = get('damage');
  const dmgIgnoreArmor = get('damageIgnoreArmor');
  const totalDmg = dmg.reduce((a: number, e: any) => a + (e.amount || 0), 0);
  if (totalDmg > 0) tags.push({ label: `DMG ${totalDmg}`, tone: 'danger' });
  const totalIgnoreArmor = dmgIgnoreArmor.reduce((a: number, e: any) => a + (e.amount || 0), 0);
  if (totalIgnoreArmor > 0) tags.push({ label: `DMG ${totalIgnoreArmor} IA`, tone: 'danger' });

  const dr = get('drawRandom');
  const drp = get('drawRandomPhysical');
  const drm = get('drawRandomMagic');
  const nRand = (dr.reduce((a: number, e: any) => a + (e.amount || 0), 0)
    + drp.reduce((a: number, e: any) => a + (e.amount || 0), 0)
    + drm.reduce((a: number, e: any) => a + (e.amount || 0), 0));
  if (nRand > 0) tags.push({ label: `DRAW RAND ${nRand}`, tone: 'info' });

  const reflect = get('reflect');
  if (reflect.length) {
    // Show highest reflect percent described on the card
    const pct = Math.max(...reflect.map((e: any) => e.percent || 0));
    if (pct > 0) tags.push({ label: `REFLECT ${pct}%`, tone: 'warn' });
  }

  const stun = get('stun');
  if (stun.length) {
    const turns = Math.max(...stun.map((e: any) => e.turns || 0));
    if (turns > 0) tags.push({ label: `STUN ${turns}`, tone: 'warn' });
  }

  // Addendum
  if (card.addendum) {
    const res: 'SP' | 'HP' = (card.addendum as any).costHp != null ? 'HP' : 'SP';
    const token = (res === 'HP' ? (card.addendum as any).costHp : (card.addendum as any).costSp) as (number | 'X' | undefined);
    const cost = token === 'X' ? 'X' : String(token ?? '');
    tags.push({ label: `ADDENDUM ${cost}${res}`, tone: 'info' });
  }

  // Keep the tag list compact
  return tags.slice(0, 5);
}

function CardView({
  card,
  onClick,
  disabled,
  mode = 'play',
  showKeywordTooltip = false,
  physicalEscalation,
  magicEscalation,
  magicChainStreak,
}: CardViewProps) {

  // Memoize derived tag list so that it only recomputes when the card
  // reference changes. The tag-building logic can be non-trivial and does not
  // depend on any transient UI state, so React.useMemo keeps it cheap.
  const tags = React.useMemo(() => buildTags(card), [card]);

  const thresholdVerifierUi = useGameStore(
    React.useCallback((store) => {
      const state: any = store.state ?? {};
      const locks = Array.isArray(state.unauthorizedPassageLocks) ? state.unauthorizedPassageLocks : [];
      return {
        unauthorizedTurns: Math.max(0, Number(locks.find((entry: any) => entry?.cardId === card.id)?.turns ?? 0) || 0),
      };
    }, [card.id]),
    shallow,
  );

  const cardCostUiState = useGameStore(
    React.useCallback((store) => {
      const state: any = store.state ?? {};
      return {
        nextCardCost0: Math.max(0, Number(state.nextCardCost0 ?? 0) || 0),
        nextMagicCardCostDoubleSP: Math.max(0, Number(state.nextMagicCardCostDoubleSP ?? 0) || 0),
        utilityCostReductionThisTurn: Math.max(0, Number(state.utilityCostReductionThisTurn ?? 0) || 0),
        chainCostReduction: Math.max(0, Number(state.relicMods?.chainCostReduction ?? 0) || 0),
        delayCostReduction: Math.max(0, Number(state.relicMods?.delayCostReduction ?? 0) || 0),
        exileCardsGetChain: !!state.relicMods?.exileCardsGetChain,
        turn: Number(state.turn ?? 0) || 0,
        phase: String(state.phase ?? ''),
        blackhandPressurePointTurn: Number(state.blackhandPressurePointTurn ?? -1),
        playerMarkedByBlackhand: !!state.playerMarkedByBlackhand,
      } as CardCostUiState;
    }, []),
    shallow,
  );

  const useCombatCostState = magicChainStreak != null || physicalEscalation != null || magicEscalation != null;

  const overloadAwareCostUi = React.useMemo(
    () => computeCardCostUi(
      { ...card, __uiMagicChainStreak: Math.max(0, Number(magicChainStreak ?? 0) || 0) } as Card,
      useCombatCostState
        ? cardCostUiState
        : {
            nextCardCost0: 0,
            nextMagicCardCostDoubleSP: 0,
            utilityCostReductionThisTurn: 0,
            chainCostReduction: 0,
            delayCostReduction: 0,
            exileCardsGetChain: false,
            turn: 0,
            phase: '',
            blackhandPressurePointTurn: -1,
            playerMarkedByBlackhand: false,
          },
    ),
    [card, cardCostUiState, magicChainStreak, physicalEscalation, magicEscalation, useCombatCostState],
  );

  const { hpBadgeText, spBadgeText, hasPressurePointHpBonus, hasPressurePointSpEcho, extraTooltipRows, cardTone } = overloadAwareCostUi;

  // Determine the native title (tooltip text) outside of keyword tooltips.
  // Memoizing this prevents string concatenation on every render.
  const unauthorizedBlocked = thresholdVerifierUi.unauthorizedTurns > 0 && mode === 'play';
  const rootFilterStyle = thresholdVerifierUi.unauthorizedTurns > 0
    ? { filter: 'brightness(0.5) saturate(0.72) contrast(0.96)' }
    : undefined;

  const nativeTitle = React.useMemo(
    () => {
      if (showKeywordTooltip) return undefined;
      const base = card.description || card.subtitle || card.title;
      const extraNotes = extraTooltipRows.length > 0
        ? `\n\n${extraTooltipRows.map((row) => `${row.label}: ${row.description}`).join('\n')}`
        : '';
      const lockNote = thresholdVerifierUi.unauthorizedTurns > 0
        ? `\n\nUnauthorized Passage: this card cannot be played for ${thresholdVerifierUi.unauthorizedTurns} more turn(s).`
        : '';
      return `${base ?? ''}${extraNotes}${lockNote}`.trim();
    },
    [card.description, card.subtitle, card.title, extraTooltipRows, showKeywordTooltip, thresholdVerifierUi.unauthorizedTurns],
  );

  const damageMaskIndex = React.useMemo(() => {
    const key = [card.id, card.title, card.kind, card.cost?.hp ?? 0, card.cost?.sp ?? 0].join('|');
    return hashToIndex(key, COMBAT_DAMAGE_MASKS.length);
  }, [card.cost?.hp, card.cost?.sp, card.id, card.kind, card.title]);

  const damageMaskImg = damageMaskIndex >= 0 ? COMBAT_DAMAGE_MASKS[damageMaskIndex] : null;
  const damageMaskOverlayImg = damageMaskIndex >= 0 ? COMBAT_DAMAGE_MASK_OVERLAYS[damageMaskIndex] : null;

  // Build the inline mask CSS for the damage effect. This object must be
  // recreated only when the underlying image changes, so we memoize it
  // based on the damageMaskImg reference.
  const damageMaskStyles = React.useMemo(
    () =>
      damageMaskImg
        ? {
            WebkitMaskImage: `url(${damageMaskImg})`,
            maskImage: `url(${damageMaskImg})`,
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
            WebkitMaskMode: 'alpha' as const,
            maskMode: 'alpha' as const,
          }
        : undefined,
    [damageMaskImg],
  );

  const content = onClick ? (
    <button
      className={`card ${mode === 'discard' && !disabled ? 'cardDiscard' : ''}`}
      disabled={disabled || unauthorizedBlocked}
      onClick={onClick}
      title={nativeTitle}
      type="button"
      style={rootFilterStyle}
    >
      <div
        className="cardDamageFrame"
        style={damageMaskStyles}
      >
        {card.img ? (
          <img
            src={card.img}
            alt={card.title}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{ pointerEvents: 'none' }}
          />
        ) : (
          <div className="cardFallback">
            <div className="cardTitle">{card.title}</div>
            <div className="cardKind">{card.kind}</div>
          </div>
        )}
        {damageMaskOverlayImg ? (
          <div
            className="cardDamageTexture"
            style={{ backgroundImage: `url(${damageMaskOverlayImg})` }}
          />
        ) : null}
        <div className="cardOverlay">
        {/* Top overlay: HP cost on the left, SP cost (and discard marker when applicable) on the right. */}
        <div className="cardTop">
          <span className={["cardCostBadge", `cardCostBadge--${cardTone}`, "cardCostBadge--hp", hasPressurePointHpBonus ? "isPressurePointOverload" : ""].filter(Boolean).join(" ")}>{hpBadgeText}</span>
          <div className="cardTopRight">
            {mode === 'discard' && !disabled && <span className="pill danger">DISCARD</span>}
            <span className={["cardCostBadge", `cardCostBadge--${cardTone}`, "cardCostBadge--sp", hasPressurePointSpEcho ? "isPressurePointEcho" : ""].filter(Boolean).join(" ")}>{spBadgeText}</span>
          </div>
        </div>
        {tags.length > 0 && (
          <div className="cardBottom">
            {tags.map((t) => (
              <span
                key={t.label}
                className={`pill keyword ${t.tone === 'danger' ? 'danger' : t.tone === 'warn' ? 'warn' : ''}`}
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
        {thresholdVerifierUi.unauthorizedTurns > 0 ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              background: 'linear-gradient(180deg, rgba(12, 8, 18, 0.34) 0%, rgba(5, 4, 8, 0.78) 100%)',
            }}
          >
            <div
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(196, 181, 253, 0.7)',
                boxShadow: '0 0 0 1px rgba(88, 28, 135, 0.24), 0 0 16px rgba(88, 28, 135, 0.26)',
                background: 'rgba(11, 8, 16, 0.94)',
                color: 'rgba(245, 243, 255, 0.98)',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Unauthorized · {thresholdVerifierUi.unauthorizedTurns}T
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </button>
  ) : (
    <div
      className={`card ${mode === 'discard' && !disabled ? 'cardDiscard' : ''}`}
      title={nativeTitle}
      aria-disabled={disabled}
      data-disabled={(disabled || unauthorizedBlocked) ? 'true' : 'false'}
      style={rootFilterStyle}
    >
      <div
        className="cardDamageFrame"
        style={damageMaskStyles}
      >
        {card.img ? (
          <img
            src={card.img}
            alt={card.title}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{ pointerEvents: 'none' }}
          />
        ) : (
          <div className="cardFallback">
            <div className="cardTitle">{card.title}</div>
            <div className="cardKind">{card.kind}</div>
          </div>
        )}
        {damageMaskOverlayImg ? (
          <div
            className="cardDamageTexture"
            style={{ backgroundImage: `url(${damageMaskOverlayImg})` }}
          />
        ) : null}
        <div className="cardOverlay">
        <div className="cardTop">
          <span className={["cardCostBadge", `cardCostBadge--${cardTone}`, "cardCostBadge--hp", hasPressurePointHpBonus ? "isPressurePointOverload" : ""].filter(Boolean).join(" ")}>{hpBadgeText}</span>
          <div className="cardTopRight">
            {mode === 'discard' && !disabled && <span className="pill danger">DISCARD</span>}
            <span className={["cardCostBadge", `cardCostBadge--${cardTone}`, "cardCostBadge--sp", hasPressurePointSpEcho ? "isPressurePointEcho" : ""].filter(Boolean).join(" ")}>{spBadgeText}</span>
          </div>
        </div>
        {tags.length > 0 && (
          <div className="cardBottom">
            {tags.map((t) => (
              <span
                key={t.label}
                className={`pill keyword ${t.tone === 'danger' ? 'danger' : t.tone === 'warn' ? 'warn' : ''}`}
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
        {thresholdVerifierUi.unauthorizedTurns > 0 ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              background: 'linear-gradient(180deg, rgba(12, 8, 18, 0.34) 0%, rgba(5, 4, 8, 0.78) 100%)',
            }}
          >
            <div
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(196, 181, 253, 0.7)',
                boxShadow: '0 0 0 1px rgba(88, 28, 135, 0.24), 0 0 16px rgba(88, 28, 135, 0.26)',
                background: 'rgba(11, 8, 16, 0.94)',
                color: 'rgba(245, 243, 255, 0.98)',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Unauthorized · {thresholdVerifierUi.unauthorizedTurns}T
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </div>
    );

  if (!showKeywordTooltip) return content;

  return (
    <div className="cardKeywordTooltipWrap">
      {content}
      <CardKeywordTooltip card={card} extraRows={extraTooltipRows} />
    </div>
  );
}

// Export a memoized version of CardView to prevent unnecessary re-renders when
// Board or Hand update unrelated state. The custom comparator ensures that
// CardView only re-renders when its inputs change in a meaningful way.
export default React.memo(CardView, areCardViewPropsEqual);
